import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type z from 'zod';

import type { ApiError, ProblemDetailsSchema } from '@/js/libs/api';
import '@/js/libs/framework/log';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useNotification } from '@/js/libs/ui/src/V2';

import { isErrorIgnored } from 'src/utils/isErrorIgnored';

interface UseRequestErrorNotificationProps {
  title?: string;
  fallbackMessage?: ReactNode;
}

export type RequestErrorNotification = (data: ApiError<typeof ProblemDetailsSchema>, isRetrying?: boolean) => void;
type UseRequestErrorNotification = (props?: UseRequestErrorNotificationProps) => RequestErrorNotification;

export function getErrorMessage<TFallback>(
  response: z.infer<typeof ProblemDetailsSchema> | undefined,
  t: TFunction,
  fallbackMessage: TFallback,
): string | TFallback;
export function getErrorMessage(response: z.infer<typeof ProblemDetailsSchema> | undefined, t: TFunction): string;
export function getErrorMessage<TFallback = void>(
  response: z.infer<typeof ProblemDetailsSchema> | undefined,
  t: TFunction,
  fallbackMessage?: TFallback,
): string | TFallback {
  const { data } = response ?? {};
  const { translationKey, message } = data ?? {};

  if (translationKey) {
    return t(translationKey);
  }

  if (message) {
    return t(message);
  }

  return fallbackMessage || t('common:UNKNOWN_ERROR');
}

/**
 * To be used with useRequest. Returns a memoized function that shows request error in a notification, translating its
 * response.data.message (most errors in our app are returned this way) or showing "Unknown error" if nothing was
 * passed.
 * @param props - props of the hook
 * @param props.title - optional notification title (should be translated outside). If not provided - "Error" will
 * be used as notification title.
 * @param props.fallbackMessage - optional message to be used instead of UNKNOWN_ERROR.
 * @returns A function to show error notification for `useRequest`.
 */
export const useRequestErrorNotification: UseRequestErrorNotification = ({ fallbackMessage, title } = {}) => {
  const notify = useNotification();
  const { t } = useTranslation(['backend', 'common']);

  return useCallbackRef<RequestErrorNotification>((apiError, isRetrying) => {
    const ignoreValidationError = isErrorIgnored(apiError) || isRetrying;

    if (ignoreValidationError) {
      console.dev.tag('requestError', 'notificationHidden').debug(apiError);
    } else {
      console.dev.tag('requestError', 'notificationShown').error(apiError);
    }

    if (ignoreValidationError) {
      return;
    }

    const body = getErrorMessage(apiError.response, t, fallbackMessage);

    notify.error({ title: title || t('common:ERROR'), body });
  });
};
